import { projectApi } from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Radio } from 'lucide-react';
import * as React from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toErrorMessage } from '@/hooks/use-form-errors';
import { SettingsSection } from '@/pages/settings/components/settings-section';

export const MonitoringTermsSection = ({
  projectId,
  canManage,
}: {
  projectId: string;
  canManage: boolean;
}) => {
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const query = useQuery({
    queryKey: ['project-monitoring-terms', projectId],
    queryFn: () => projectApi.getMonitoringTerms(projectId),
  });
  const mutation = useMutation({
    mutationFn: (terms: string[]) =>
      projectApi.updateMonitoringTerms(projectId, { terms }),
    onSuccess: (data) => {
      queryClient.setQueryData(['project-monitoring-terms', projectId], data);
      setError(null);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
    onError: (cause) => setError(toErrorMessage(cause)),
  });
  if (query.isLoading) {
    return <Skeleton className="h-56 rounded-card" />;
  }
  if (!query.data) {
    return null;
  }

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    mutation.mutate(
      String(form.get('terms') ?? '')
        .split(/[,\n]/)
        .map((term) => term.trim())
        .filter(Boolean),
    );
  };

  return (
    <SettingsSection
      title="External monitoring"
      description="Watch public sources for mentions that can explain traffic changes."
      onSubmit={submit}
      footer={
        canManage ? (
          <>
            {saved ? (
              <span className="mr-auto text-sm text-positive">Saved</span>
            ) : null}
            <Button type="submit" loading={mutation.isPending}>
              Save terms
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-ink">
            <Radio className="size-4 text-positive" />
            Always monitored
          </div>
          <div className="flex flex-wrap gap-2">
            {query.data.automaticTerms.map((term) => (
              <Badge key={term} tone="neutral">
                {term}
              </Badge>
            ))}
          </div>
        </div>
        <Field
          label="Extra terms"
          htmlFor="monitoring-terms"
          hint="Product names, founder names or campaign phrases. Separate with commas."
        >
          <Input
            id="monitoring-terms"
            name="terms"
            defaultValue={query.data.customTerms.join(', ')}
            placeholder="Acme AI, Jane Founder"
            disabled={!canManage}
          />
        </Field>
      </div>
    </SettingsSection>
  );
};
